import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectAllAccounts, selectExternalAccounts, useGetAccountsQuery, useGetExternalAccountsQuery } from "fond/api";
import { Account, ExternalAccount, UserRole } from "fond/types";
import { usePinnedAccountIds } from "fond/views/Accounts/hooks/usePinnedAccountIds";

import { useAccountRoles } from "./useAccountRoles";

export type SwitchAccount = (Account | ExternalAccount) & { External: boolean; Role?: UserRole };

export interface UseSwitchableAccountsOptions {
  /**
   * Controls sorting and filtering of accounts:
   * - "all": Returns all accounts with pinned accounts first
   * - "pinned-only": Returns only pinned accounts (empty if none pinned)
   * - "pinned-only-or-all": Returns pinned accounts if any exist, otherwise all accounts
   * @default "all"
   */
  mode?: "all" | "pinned-only" | "pinned-only-or-all";
}

export interface UseSwitchableAccountsResult {
  isLoading: boolean;
  accounts: SwitchAccount[];
}

/**
 * Gets all accounts available for switching, including both user member accounts and external accounts
 * @param options - Optional sorting and filtering options
 * @returns Object with isLoading state and array of account objects with External flag
 * - Member accounts: Full Account objects with all fields
 * - External accounts: ExternalAccount objects with ID, Name, Logo, ForegroundColor, BackgroundColor
 */
export const useSwitchableAccounts = (options?: UseSwitchableAccountsOptions): UseSwitchableAccountsResult => {
  const { isLoading: isLoadingAccounts } = useGetAccountsQuery();
  const { isLoading: isLoadingExternalAccounts } = useGetExternalAccountsQuery();
  const userAccounts = useSelector(selectAllAccounts);
  const externalAccounts = useSelector(selectExternalAccounts);
  const pinnedAccountIds = usePinnedAccountIds();
  const accountRoles = useAccountRoles();

  const isLoading = isLoadingAccounts || isLoadingExternalAccounts;
  const { mode = "all" } = options ?? {};

  const accounts = useMemo(() => {
    const accountsMap = new Map<string, SwitchAccount>();
    const userAccountIds = new Set(userAccounts.map((account) => account.ID));

    // Add all user member accounts (full Account objects with all fields)
    userAccounts.forEach((account) => {
      accountsMap.set(account.ID, {
        ...account,
        External: false,
        Role: accountRoles.get(account.ID),
      });
    });

    // Add external accounts from items where user is not a member (ExternalAccount with visual branding fields)
    externalAccounts.forEach((account) => {
      if (!userAccountIds.has(account.ID)) {
        accountsMap.set(account.ID, {
          ...account,
          External: true,
        });
      }
    });

    let allAccounts = Array.from(accountsMap.values());

    // Precompute Set and Map for O(1) lookups instead of O(m) array operations
    const pinnedSet = new Set(pinnedAccountIds);
    const pinnedOrderMap = new Map(pinnedAccountIds.map((id, index) => [id, index]));

    // Filter to pinned accounts
    const pinnedAccounts = allAccounts.filter((account) => pinnedSet.has(account.ID));

    // Sort pinned accounts by their order in the pinned list
    const sortedPinnedAccounts = pinnedAccounts.sort((a, b) => {
      const indexA = pinnedOrderMap.get(a.ID)!;
      const indexB = pinnedOrderMap.get(b.ID)!;
      return indexA - indexB;
    });

    // If mode is "pinned-only", return only pinned accounts (empty if none)
    if (mode === "pinned-only") {
      return sortedPinnedAccounts;
    }

    // If mode is "pinned-only-or-all", return pinned accounts if any, otherwise all
    if (mode === "pinned-only-or-all") {
      return sortedPinnedAccounts.length > 0 ? sortedPinnedAccounts : allAccounts;
    }

    // Default (mode is "all"): return all accounts with pinned ones first
    const unpinnedAccounts = allAccounts.filter((account) => !pinnedSet.has(account.ID));
    return [...sortedPinnedAccounts, ...unpinnedAccounts];
  }, [userAccounts, externalAccounts, accountRoles, mode, pinnedAccountIds]);

  return { isLoading, accounts };
};
